import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigationType } from 'react-router-dom'
import { scrollStore } from '@/lib/scrollStore'
import { useRestoration } from '@/components/layout/RestorationProvider'

export const MAX_RESTORE_DEPTH = 10

interface Options<TPage> {
  /** Must be the exact key the page's useInfiniteQuery computes. */
  queryKey: readonly unknown[]
  /** Fetch a single page. Same call the page's queryFn makes. */
  fetchPage: (page: number) => Promise<TPage>
  /** Current number of loaded pages (data.pages.length ?? 1). */
  pageDepth: number
}

/**
 * Reports infinite-scroll depth into the scroll snapshot, and on a POP with a
 * cold cache re-seeds pages 1..N in PARALLEL (possible because pagination is
 * page-number based, not cursor based) before releasing the restore gate.
 */
export function useRestorableList<TPage>({ queryKey, fetchPage, pageDepth }: Options<TPage>) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigationType = useNavigationType()
  const { reportDepth, acquireGate, cancelRestore } = useRestoration()
  const restoredForKey = useRef<string | null>(null)

  useEffect(() => {
    reportDepth(pageDepth)
  }, [pageDepth, reportDepth])

  useEffect(() => {
    if (navigationType !== 'POP') return
    if (restoredForKey.current === location.key) return
    restoredForKey.current = location.key

    // Same guard as the provider: a snapshot from another pathname must not be
    // trusted, or we would re-seed pages 1..N of the WRONG resource.
    const raw = scrollStore.get(location.key)
    const snap = raw && raw.pathname === location.pathname ? raw : undefined
    if (!snap || snap.pageDepth <= 1) return

    // A PARTIALLY warm cache is the trap: after a reload with the lightbox open, the
    // feed refetches page 1 only, so "has data" is true while the snapshot says 3 pages.
    // Bailing there leaves the restore scrolling toward an offset the 20-row list cannot
    // reach — the browser clamps it to the bottom of page 1 and pages 2..N are gone.
    // Only skip when the cache is already at least as deep as the snapshot.
    const cached = queryClient.getQueryData(queryKey) as { pages?: unknown[] } | undefined
    if ((cached?.pages?.length ?? 0) >= snap.pageDepth) return

    if (snap.pageDepth > MAX_RESTORE_DEPTH) {
      // Too deep to re-seed without storming the API. Land at the top rather than let
      // the provider scroll toward an offset this shallow list can't reach (which the
      // browser clamps to the BOTTOM of page 1 — the opposite of what we want).
      cancelRestore()
      return
    }

    const release = acquireGate()
    const pages = Array.from({ length: snap.pageDepth }, (_, i) => i + 1)

    Promise.allSettled(pages.map((p) => fetchPage(p)))
      .then(async (results) => {
        const seeded: TPage[] = []
        const pageParams: number[] = []
        for (let i = 0; i < results.length; i++) {
          const r = results[i]
          if (r.status !== 'fulfilled') break // seed up to the first gap
          seeded.push(r.value)
          pageParams.push(i + 1)
        }
        // Nothing to restore from: leave the page's own query completely alone so
        // it resolves normally into page 1. (Cancelling it here would strand the
        // list on an empty state with no data, no error and nothing to retry it.)
        if (seeded.length === 0) return

        // The page's own useInfiniteQuery is fetching page 1 against this same key.
        // Its response would land AFTER our seed and overwrite `data` with
        // { pages: [p1] } — collapsing the list to 20 rows and clamping the restored
        // offset to the bottom of page 1, the exact failure the gate exists to prevent.
        //
        // Cancel LATE — immediately before the write, not before the fetches. A cancel
        // reverts the query to pending+idle, i.e. `isLoading === false` with no data,
        // which renders the "no events" empty state for as long as it is held. Doing
        // it here keeps the query in its loading state (skeletons) for the whole
        // restore, and the seed lands in the same batch.
        await queryClient.cancelQueries({ queryKey })
        queryClient.setQueryData(queryKey, { pages: seeded, pageParams })
      })
      .catch(() => {
        // Restoration is best-effort; never surface an error for it.
      })
      .finally(release)
    // queryKey is a stable literal per filter set; re-running on key change is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, navigationType])
}
